// base-http.service.ts
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Capacitor, CapacitorHttp, HttpOptions } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { BehaviorSubject, fromEvent, lastValueFrom, map, merge, Observable, startWith } from 'rxjs';
import * as CryptoJS from 'crypto-js';
import 'crypto-js/enc-base64';   // 👈 this makes CryptoJS.enc.Base64 available
import { jwtDecode } from 'jwt-decode';
import { AuthResponse, EncryptedData, JwtPayload } from './auth.service';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { MatSnackBar } from '@angular/material/snack-bar';
import { environment } from '../../environments/environment';

export class PageResponse<T> {
  totalPages: number;
  currentPage: number;
  totalItems: number;
  data: T[] = [];
}


export abstract class BaseHttpService {
  private readonly online = new BehaviorSubject<boolean>(navigator.onLine);
  private readonly loading = new BehaviorSubject<boolean>(false);
  protected readonly AUTH_TOKEN: string;
  protected readonly PROJECT: string;
  protected readonly KEY: string;
  protected readonly VERIFICATION: string;
  protected readonly PROMO: string;

  private readonly MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB
  private readonly MAX_TOTAL_SIZE = 10 * 1024 * 1024; // 20 MB
  private readonly ALLOWED_TYPES = [
    'image/png',
    'image/jpeg',
    'image/jpg',
    'image/gif',
    'image/webp'
  ];


  constructor(protected client: HttpClient, protected snackBar: MatSnackBar) {
    this.KEY = environment.server.key;
    //this.PROJECT = environment.server.project;
    this.AUTH_TOKEN = environment.server.auth_token;
    this.VERIFICATION = 'VERIFICATION';
    this.PROMO = 'PROMO';

    // Wait for the Capacitor bridge

  }


  protected async getMediaAuthHeaders(): Promise<Record<string, string>> {
    const authResponse = null;//await this.getLatestAuthToken();

    const headers: { [key: string]: string } = {
    };

    if (authResponse?.token) {
      headers['Authorization'] = `Bearer ${authResponse.token}`;
    }

    return headers;
  }


  protected async getAuthHeaders(): Promise<Record<string, string>> {
    const authResponse = null;//await this.getLatestAuthToken();

    const headers: { [key: string]: string } = {
      'Content-Type': 'application/json',
    };

    if (authResponse?.token) {
      headers['Authorization'] = `Bearer ${authResponse.token}`;
    }

    return headers;
  }

  /*
  protected async getLatestAuthToken(): Promise<AuthResponse> {
    let token = await this.retrieveAuthToken();

    if (!token) {
      return token;
    }

    if (this.isTokenExpired(token.token)) {
      token = await this.refreshToken(token.token);
      this.storeAuthToken(token);
    }

    return token;
  }
  /*

  protected async refreshToken(token: string): Promise<AuthResponse> {
    const urlEndPoint = environment.server.mainURL + 'commons/users/refresh-token';
    const headers = {
      'Content-Type': 'application/json',
      'Project': this.PROJECT
    };

    let body = {
      token: token
    };
    return this.post<AuthResponse>(urlEndPoint, body, null, headers);
  }
  */

  openSnackBar(message: string, action: string) {
    this.snackBar.open(message, action, { duration: 4000 });
  }

  listenToInternetConnection() {
    const onlineEvt = fromEvent(window, 'online');
    const offlineEvt = fromEvent(window, 'offline');

    merge(onlineEvt, offlineEvt)
      .pipe(
        map(() => navigator.onLine),
        startWith(navigator.onLine)
      )
      .subscribe(isOnline => {
        // Re‑enter Angular zone so UI updates happen
        this.online.next(isOnline);
      });
  }

  /** Observable you can subscribe to */
  getOnlineState(): Observable<boolean> {
    return this.online.asObservable();
  }

  getLoadingState(): Observable<boolean> {
    return this.loading.asObservable();
  }

  async get<T>(url: string, params?: object, headers?: Record<string, string>, credentials?: 'include' | 'omit' | 'same-origin') {
    const effectiveCredentials = credentials ?? 'include';

    if (Capacitor.isNativePlatform()) {

      const options: HttpOptions = { url: url, webFetchExtra: { credentials: credentials } };
      options.params = params ? this.sanatizeParams(params) : null;
      options.headers = headers ? headers : await this.getAuthHeaders();
      options.webFetchExtra.credentials = effectiveCredentials;

      const res = await CapacitorHttp.get(options);
      return res.data as T
    }
    const options = {
      params: params ? this.buildHttpParams(params) : null,
      headers: headers ? new HttpHeaders(headers) : new HttpHeaders(await this.getAuthHeaders()),
      withCredentials: effectiveCredentials === 'include'
    }
    return lastValueFrom(this.client.get<T>(url, options));
  }

  async post<T>(url: string, body: any, params?: object, headers?: Record<string, string>, credentials?: 'include' | 'omit' | 'same-origin') {
    const effectiveCredentials = credentials ?? 'include';

    if (Capacitor.isNativePlatform()) {
      const options: HttpOptions = { url: url, data: body, webFetchExtra: { credentials: effectiveCredentials } };
      options.params = params ? this.sanatizeParams(params) : null;
      options.headers = headers ? headers : await this.getAuthHeaders();
      options.webFetchExtra.credentials = effectiveCredentials;

      const res = await CapacitorHttp.post(options);
      return res.data as T;
    }
    const options = {
      params: params ? this.buildHttpParams(params) : null,
      headers: headers ? new HttpHeaders(headers) : new HttpHeaders(await this.getAuthHeaders()),
      withCredentials: effectiveCredentials === 'include'
    }
    return lastValueFrom(this.client.post<T>(url, body, options));
  }

  async put<T>(url: string, body: any, params?: object, headers?: Record<string, string>, credentials?: 'include' | 'omit' | 'same-origin') {
    const effectiveCredentials = credentials ?? 'include';

    if (Capacitor.isNativePlatform()) {
      const options: HttpOptions = { url: url, webFetchExtra: { credentials: credentials } };
      options.params = params ? this.sanatizeParams(params) : null;
      options.headers = headers ? headers : await this.getAuthHeaders();
      options.webFetchExtra.credentials = effectiveCredentials;
      const res = await CapacitorHttp.put(options);
      return res.data as T;
    }
    const options = {
      params: params ? this.buildHttpParams(params) : null,
      headers: headers ? new HttpHeaders(headers) : new HttpHeaders(await this.getAuthHeaders()),
      withCredentials: effectiveCredentials === 'include'
    }

    return lastValueFrom(this.client.put<T>(url, body, options));
  }

  async delete<T>(url: string, params?: object, headers?: Record<string, string>, credentials?: 'include' | 'omit' | 'same-origin') {
    const effectiveCredentials = credentials ?? 'include';

    if (Capacitor.isNativePlatform()) {
      const options: HttpOptions = { url: url, webFetchExtra: { credentials: credentials } };
      options.params = params ? this.sanatizeParams(params) : null;
      options.headers = headers ? headers : await this.getAuthHeaders();
      options.webFetchExtra.credentials = effectiveCredentials;
      const res = await CapacitorHttp.put(options);
      return res.data as T;
    }
    const options = {
      params: params ? this.buildHttpParams(params) : null,
      headers: headers ? new HttpHeaders(headers) : new HttpHeaders(await this.getAuthHeaders()),
      withCredentials: effectiveCredentials === 'include'
    }
    return lastValueFrom(this.client.delete<T>(url, options));
  }
  private buildHttpParams(paramsObj: { [key: string]: any }): HttpParams {
    let params = new HttpParams();

    Object.entries(paramsObj).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        params = params.set(key, value.toString());
      }
    });

    return params;
  }

  private sanatizeParams(paramsObj: { [key: string]: any }) {
    return Object.keys(paramsObj)
      .filter((key) => paramsObj[key] !== null && paramsObj[key] !== undefined)
      .reduce((acc, key) => {
        acc[key] = paramsObj[key];
        return acc;
      }, {} as Record<string, any>);
  }

  protected isTokenExpired(token: string): boolean {
    const decoded: JwtPayload = this.decodeToken(token);
    if (!decoded.exp) {
      return true; // no exp means invalid/expired
    }

    const nowInSeconds = Math.floor(Date.now() / 1000);
    const buffer = 60;
    return decoded.exp < (nowInSeconds + buffer);
  }

  isSubExpired(token: string) {
    const decoded: JwtPayload = this.decodeToken(token);

    if (!decoded.sub_exp) {
      return true; // no exp means invalid/expired
    }

    const nowInSeconds = Math.floor(Date.now() / 1000);
    return decoded.sub_exp < nowInSeconds;
  }

  decodeToken(token: string): JwtPayload {
    try {
      return jwtDecode<JwtPayload>(token);
    } catch (e) {
      return null;
    }
  }

  async storeAuthToken(token: AuthResponse) {
    if (Capacitor.isNativePlatform()) {
      const json = JSON.stringify(token);
      try {
        await SecureStoragePlugin.set({ key: this.AUTH_TOKEN, value: json });
      } catch (err) {
        return null;
      }

      return;
    }
    const tokenResponse: string = this.encryptPayLoad<AuthResponse>(token, this.KEY);
    await Preferences.set({ key: this.AUTH_TOKEN, value: tokenResponse });
  }

  protected async removeAuthToken() {
    if (Capacitor.isNativePlatform()) {
      await SecureStoragePlugin.remove({ key: this.AUTH_TOKEN });
      return;
    }
    await Preferences.remove({ key: this.AUTH_TOKEN });
  }

  async retrieveAuthToken(): Promise<AuthResponse> {
    if (Capacitor.isNativePlatform()) {
      try {

        const response = await SecureStoragePlugin.get({ key: this.AUTH_TOKEN });

        if (!response?.value) {
          console.log('retrieve no value');
          return null;
        }

        let parsed: unknown;
        try {
          parsed = JSON.parse(response.value);
        } catch (err) {
          console.log('Retrieve err: ', err);
          return null;
        }


        // Ensure it's actually an object (and not boolean/number/etc)
        if (typeof parsed !== 'object' || parsed === null) {
          console.log('No object');
          return null;
        }
        return parsed as AuthResponse;

      } catch (err) {
        console.log('Retireve err: ', err);
        return null;
      }

    }
    const { value } = await Preferences.get({ key: this.AUTH_TOKEN });

    if (value) {
      let response = this.decryptPayload<AuthResponse>(value, this.KEY);
      return response;
    }
    return null;
  }

  protected encryptPayLoad<T>(payload: T, secretKey: string): string {
    const payloadString = JSON.stringify(payload);
    const encrypted = CryptoJS.AES.encrypt(payloadString, secretKey);
    // Force ciphertext to Base64
    return encrypted.toString();
  }

  protected decryptPayload<T>(encryptedPayload: string, secretKey: string): T {
    try {
      const bytes = CryptoJS.AES.decrypt(encryptedPayload, secretKey);
      const decrypted: string = bytes.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decrypted) as T;
    } catch (err) {
      return null;
    }
  }

  /**
    * Only passwords will get hashed
    * @param value 
    * @returns 
    */
  protected hashValue(value: string): string {
    return CryptoJS.SHA256(value).toString(CryptoJS.enc.Hex);
  }

  protected encrypt<T>(payload: T, secretKey: string) {
    const payloadString = JSON.stringify(payload);
    const key = CryptoJS.enc.Hex.parse(secretKey);
    // Generate random 16-byte IV
    const iv = CryptoJS.lib.WordArray.random(16);
    // Encrypt
    const encrypted = CryptoJS.AES.encrypt(payloadString, key, {
      iv: iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7,
    });
    // Concatenate IV + ciphertext and encode in Base64
    const combined = iv.concat(encrypted.ciphertext);
    return CryptoJS.enc.Base64.stringify(combined);
  }

  protected decrypt<T>(combinedBase64: string, secretKey: string) {
    // Decode Base64
    const combined = CryptoJS.enc.Base64.parse(combinedBase64);
    // Extract IV (first 16 bytes = 128 bits)
    const iv = CryptoJS.lib.WordArray.create(combined.words.slice(0, 4), 16);
    // Extract ciphertext (remaining bytes)
    const payload = CryptoJS.lib.WordArray.create(combined.words.slice(4), combined.sigBytes - 16);
    return this.decryptResponse<T>(payload, secretKey, iv.toString(CryptoJS.enc.Base64));
  }

  protected encryptRequest<T>(payload: T, secretKey: string): EncryptedData {
    const payloadString = JSON.stringify(payload);
    const key = CryptoJS.enc.Hex.parse(secretKey);
    const iv = CryptoJS.lib.WordArray.random(16);

    const encrypted = CryptoJS.AES.encrypt(payloadString, key, {
      iv,
      mode: CryptoJS.mode.CBC,
      padding: CryptoJS.pad.Pkcs7
    });

    return {
      iv: iv.toString(CryptoJS.enc.Base64),
      payload: encrypted.ciphertext.toString(CryptoJS.enc.Base64)
    };

  }

  protected decryptResponse<T>(payload: string, secretKey: string, ivKey: string): T {

    try {
      const key = CryptoJS.enc.Hex.parse(secretKey);
      const iv = CryptoJS.enc.Base64.parse(ivKey);
      const ciphertext = CryptoJS.enc.Base64.parse(payload);

      const encrypted = CryptoJS.lib.CipherParams.create({
        ciphertext: ciphertext,
        formatter: CryptoJS.format.OpenSSL // Not strictly required
      });

      const decrypted = CryptoJS.AES.decrypt(encrypted, key, {
        iv: iv,
        mode: CryptoJS.mode.CBC,
        padding: CryptoJS.pad.Pkcs7
      });

      const decryptedText = decrypted.toString(CryptoJS.enc.Utf8);

      return JSON.parse(decryptedText);
    } catch (err) {
      return null;
    }
  }

  protected fileToBase64(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(',')[1]); // strip prefix
      reader.onerror = (err) => reject(err);
      reader.readAsDataURL(file);
    });
  }

  validateFiles(files: File[]): string[] {
    const errors: string[] = [];

    let totalSize = 0;

    for (const file of files) {
      // ✅ Check type
      if (!this.ALLOWED_TYPES.includes(file.type)) {
        errors.push(`File ${file.name} has unsupported type (${file.type}).`);
      }

      // ✅ Check size per file
      if (file.size > this.MAX_FILE_SIZE) {
        errors.push(`File ${file.name} is too large. Max allowed is 10 MB.`);
      }

      totalSize += file.size;
    }

    // ✅ Check total size
    if (totalSize > this.MAX_TOTAL_SIZE) {
      errors.push(
        `Total upload size exceeds ${this.MAX_TOTAL_SIZE / (1024 * 1024)} MB.`
      );
    }

    return errors;
  }


  // Add httpPut, httpDelete similarly
}


